"use client";

import React, { useEffect, useState } from "react";
import { Search, User, Phone, Globe, MessageSquare, ChevronDown, ChevronRight, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
type MenuItem = {
  id: string | number;
  label: string;
  href?: string;
  children?: MenuItem[];
};
const translationMap: Record<string, string> = {
  "Ոլորտներ": "Sectors",
  "Ծառայություններ": "Services",
  "Մեր Մասին": "About Us",
  "ՄԵր Մասին": "About Us",
  "Ռելիեֆի և տեղանքի թվային մոդելների ստեղծում DSM/DTM": "Relief and Terrain Digital Models Creation (DSM/DTM)",
  "Ռելիեֆի և տեղանքի թվային մոդելների ստեղծում  DSM/ DTM": "Relief and Terrain Digital Models Creation (DSM/DTM)",
  "Թվային քարտեզների ստեղծում և թարմացում": "Digital Maps Creation and Update",
  "Գեոդեզիական աշխատանքներ": "Geodetic Works",
  "Վերգետնյա կայաններ": "Ground Stations",
  "Ֆոտոգրամետրիական աշխատանքներ": "Photogrammetry Works",
  "Արբանյակային պատկերների ստացում": "Satellite Image Acquisition",
  "Արմսատ-1 արբանյակ": "Armsat-1 Satellite",
  "Արմսաթ-1 արբանյակ": "Armsat-1 Satellite"
};
function RecursiveDropdown({
  label,
  items,
  depth = 0
}: {
  label: string;
  items: MenuItem[];
  depth?: number;
}) {
  const [openIndex, setOpenIndex] = useState<string | number | null>(null);
  if (depth >= 2) return null;
  return <div className={cn("bg-background border border-primary/20 shadow-xl rounded-lg p-1 min-w-[240px] z-50", depth === 0 ? "mt-2" : "")}>
      {items.map((item, i) => {
      const hasChildren = !!item.children?.length && depth < 1;
      const isOpen = openIndex === item.id;
      return <div key={item.id} data-index={i} className="relative" onMouseEnter={() => hasChildren && setOpenIndex(item.id)} onMouseLeave={() => hasChildren && setOpenIndex(null)}>
            {hasChildren ? <div className="flex items-center justify-between w-full">
                <Link to={item.href || "#"} className="flex-grow block rounded-md p-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                  {item.label}
                </Link>
                <Button variant="ghost" size="icon" className="h-8 w-8 p-0 hover:bg-accent" onClick={e => {
            e.preventDefault();
            setOpenIndex(isOpen ? null : item.id);
          }}>
                  <ChevronRight className={cn("w-4 h-4 transition-transform", isOpen && "rotate-90")} />
                </Button>
              </div> : <Link to={item.href || "#"} className="block rounded-md p-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                {item.label}
              </Link>}

            {hasChildren && isOpen && <div className="absolute left-full top-0 pl-2">
                <RecursiveDropdown label={item.label} items={item.children!} depth={depth + 1} />
              </div>}
          </div>;
    })}
    </div>;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [activeMenu, setActiveMenu] = useState<string | number | null>(null);
  const isEnglish = typeof window !== "undefined" && window.location.pathname.includes("En");
  if (loading) {
    return <div className="flex gap-4 py-2">
        {[1, 2, 3, 4, 5].map(i => <div key={i} data-index={i} className="h-6 w-24 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const transformMenuItem = (item: MenuItem): MenuItem => {
    let href = item.href;
    if (item.label === "Ոլորտներ") {
      href = isEnglish ? "/HomeEn#ոլորտներ" : "/#ոլորտներ";
    }
    const label = isEnglish ? translationMap[item.label] || item.label : item.label;
    const children = item.children?.map(transformMenuItem);
    return {
      ...item,
      label,
      href,
      ...(children ? {
        children
      } : {})
    };
  };
  const transformedMenuItems = menuItems.map(transformMenuItem);
  return <div className="flex items-center gap-4">
      <NavigationMenu viewport={false} className="hidden md:flex w-full max-w-full justify-start md:justify-center pb-2 md:pb-0">
        <NavigationMenuList className="gap-6 md:gap-8">
          {transformedMenuItems.map((item, i) => {
          const hasChildren = !!item.children?.length;
          if (hasChildren) {
            return <NavigationMenuItem key={item.id} data-index={i} onMouseEnter={() => setActiveMenu(item.id)} onMouseLeave={() => setActiveMenu(null)}>
                  <div className="relative flex items-center gap-1 py-2">
                    <NavigationMenuLink asChild>
                      <Link to={item.href || "#"} className="text-sm font-default font-bold uppercase tracking-widest text-foreground transition-all duration-300 hover:text-primary border-b-2 border-transparent hover:border-primary whitespace-nowrap">
                        {item.label}
                      </Link>
                    </NavigationMenuLink>
                    <ChevronDown className={cn("w-4 h-4 transition-transform duration-300 text-foreground/50", activeMenu === item.id && "rotate-180")} />
                    
                    {activeMenu === item.id && <div className="absolute left-0 top-full z-[9999]">
                        <RecursiveDropdown label={item.label} items={item.children!} depth={0} />
                      </div>}
                  </div>
                </NavigationMenuItem>;
          }
          return <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild> 
                  <Link to={item.href || "#"} className="text-sm font-default font-bold uppercase tracking-widest text-foreground transition-all duration-300 hover:text-primary border-b-2 border-transparent hover:border-primary whitespace-nowrap py-2">
                    {item.label} 
                  </Link> 
                </NavigationMenuLink> 
              </NavigationMenuItem>;
        })}
        </NavigationMenuList>
      </NavigationMenu>

      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-primary">
              <Menu className="size-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-background border-primary/20">
            {transformedMenuItems.map((item, i) => <DropdownMenuItem key={item.id} data-index={i} asChild>
                <Link to={item.href || "#"} className="w-full font-bold uppercase tracking-wider text-xs">
                  {item.label}
                </Link>
              </DropdownMenuItem>)}
            <DropdownMenuItem asChild>
              <Button asChild className="w-full mt-2 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs rounded-lg">
                <Link to={isEnglish ? "/HomeEn#contactrequest" : "/#contactrequest"}>
                  {isEnglish ? "Contact Us" : "Կապ մեզ հետ"}
                </Link>
              </Button>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [searchValue, setSearchValue] = useState("");
  const isEnglish = typeof window !== "undefined" && window.location.pathname.includes("En");
  useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const targetId = decodeURIComponent(window.location.hash.substring(1));
        const element = document.getElementById(targetId);
        if (element) {
          const header = document.getElementById("header");
          const headerHeight = header ? header.offsetHeight : 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerHeight;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }, 300);
    }
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const targetId = decodeURIComponent(hash.substring(1));
          const element = document.getElementById(targetId);
          if (element) {
            e.preventDefault();
            e.stopPropagation();
            const header = document.getElementById("header");
            const headerHeight = header ? header.offsetHeight : 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - headerHeight;
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick, true);
    return () => document.removeEventListener("click", handleAnchorClick, true);
  }, []);
  return <header data-section-id="252528" id="header" className="sticky top-0 z-50 w-full bg-background/80 backdrop-blur-md text-foreground border-b border-primary/20 shadow-md transition-all duration-300 flex flex-col">
      <div className="flex items-center justify-between gap-4 md:gap-6 px-2 md:px-4">
        <Link to="/" className="!h-[clamp(35px,8vw,60px)] flex items-center">
          <WvcLogo className="flex-shrink-0 w-auto !h-[clamp(35px,8vw,60px)] !max-h-none [&_svg]:flex-shrink-0 [&_svg]:w-auto [&_svg]:!h-[clamp(35px,8vw,60px)]" />
        </Link>
        
        <div className="flex-grow">
          <div className="container mx-auto py-2 md:py-3 border-t border-primary/10">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>
        </div>

        <div className="flex items-center gap-4 ml-auto flex-shrink-0">
          <Button asChild variant="ghost" size="icon" className="text-primary hover:text-primary/80">
            <Link to={isEnglish ? "/" : "/HomeEn"}>
              <Globe className="!size-[clamp(20px,5vw,24px)]" />
            </Link>
          </Button>
          <Button asChild className="hidden md:flex bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider text-xs rounded-lg">
            <Link to={isEnglish ? "/HomeEn#contactrequest" : "/#contactrequest"}>
              {isEnglish ? "Contact Us" : "Կապ մեզ հետ"}
            </Link>
          </Button>
        </div>
      </div>
    </header>;
}